import { Form, Input, Select } from 'antd'
import React from 'react'
import { useDispatch, useSelector } from 'react-redux'
import styled from 'styled-components'
import { stationApi } from '../../../../../apis'
import { ActionEnum, AlertMessageEnum } from '../../../../../common'
import {
  ManageModalComponent,
  ManageModalFooterComponent,
} from '../../../../../components'
import { translate } from '../../../../../languages'
import { StationEntity, UserTypeEnum } from '../../../../../models'
import { RootState } from '../../../../../stores'
import {
  loadUsers,
  setAction,
  setActionProcessing,
  setAlert,
  setSelectedStation,
  setStations,
} from '../../../../../stores/actions'

const StyledFormItem = styled(Form.Item)`
  margin-top: 5px;
`

const StyledLabel = styled.span`
  font-weight: bold;
`

type InputStation = {
  name: string
  address: string
  ownerId: number
}

export const ManageStationModalComponent: React.FC = () => {
  const dispatch = useDispatch()
  const [form] = Form.useForm()
  const { selectedStation, stations } = useSelector(
    (state: RootState) => state.station
  )
  const { language } = useSelector((state: RootState) => state.language)
  const { management } = useSelector((state: RootState) => state.common)
  const { users, isUsersLoaded } = useSelector((state: RootState) => state.user)
  const readOnlyMode = React.useMemo(
    () => [ActionEnum.VIEW, ActionEnum.DELETE].includes(management.action),
    [management]
  )

  React.useEffect(() => {
    if (!isUsersLoaded) {
      dispatch(loadUsers())
    }
    // eslint-disable-next-line
  }, [])

  const replaceStation = (station: StationEntity) => {
    const index = stations.map((item) => item.id).indexOf(station.id)
    if (index > -1) {
      dispatch(
        setStations([
          ...stations.slice(0, index),
          station,
          ...stations.slice(index + 1),
        ])
      )
    }
  }

  const onFinish = async (value: InputStation) => {
    dispatch(setAlert())
    dispatch(setActionProcessing(true))
    switch (management.action) {
      case ActionEnum.VIEW:
        dispatch(setAction(ActionEnum.UPDATE))
        break
      case ActionEnum.UPDATE:
        if (selectedStation) {
          try {
            const respUpdate = await stationApi.updateStation(
              selectedStation.id,
              value
            )
            replaceStation(respUpdate.data)
            dispatch(setSelectedStation())
            dispatch(
              setAlert({
                key: 'station.update.success',
                type: AlertMessageEnum.SUCCESS,
              })
            )
          } catch (e) {
            if (e && e.response && e.response.status === 401) {
              const respUpdate = await stationApi.updateStation(
                selectedStation.id,
                value
              )
              replaceStation(respUpdate.data)
              dispatch(setSelectedStation())
              dispatch(
                setAlert({
                  key: 'station.update.success',
                  type: AlertMessageEnum.SUCCESS,
                })
              )
            } else if (e && e.response && e.response.status === 409) {
              dispatch(
                setAlert({
                  key: 'station.update.fail.duplicate',
                  type: AlertMessageEnum.ERROR,
                })
              )
            } else {
              dispatch(
                setAlert({
                  key: 'station.update.fail.internal',
                  type: AlertMessageEnum.ERROR,
                })
              )
            }
          }
        }
        break
      case ActionEnum.CREATE:
        try {
          const respCreate = await stationApi.createStation(value)
          dispatch(setStations([respCreate.data, ...stations]))
          dispatch(setSelectedStation())
          dispatch(
            setAlert({
              key: 'station.create.success',
              type: AlertMessageEnum.SUCCESS,
            })
          )
        } catch (e) {
          if (e && e.response && e.response.status === 401) {
            const respCreate = await stationApi.createStation(value)
            dispatch(setStations([respCreate.data, ...stations]))
            dispatch(setSelectedStation())
            dispatch(
              setAlert({
                key: 'station.create.success',
                type: AlertMessageEnum.SUCCESS,
              })
            )
          } else if (e && e.response && e.response.status === 409) {
            dispatch(
              setAlert({
                key: 'station.create.fail.duplicate',
                type: AlertMessageEnum.ERROR,
              })
            )
          } else {
            dispatch(
              setAlert({
                key: 'station.create.fail.internal',
                type: AlertMessageEnum.ERROR,
              })
            )
          }
        }
        break
      case ActionEnum.DELETE:
        try {
          await stationApi.deleteStation(selectedStation?.id || 0)
          dispatch(
            setStations(
              stations.filter((item) => item.id !== selectedStation?.id)
            )
          )
          dispatch(setSelectedStation())
          dispatch(
            setAlert({
              key: 'station.delete.success',
              type: AlertMessageEnum.SUCCESS,
            })
          )
        } catch (e) {
          if (e.response && e.response.status && e.response.status === 401) {
            await stationApi.deleteStation(selectedStation?.id || 0)
            dispatch(
              setStations(
                stations.filter((item) => item.id !== selectedStation?.id)
              )
            )
            dispatch(setSelectedStation())
            dispatch(
              setAlert({
                key: 'station.delete.success',
                type: AlertMessageEnum.SUCCESS,
              })
            )
          } else {
            dispatch(
              setAlert({
                key:
                  e.response && e.response.status && e.response.status === 400
                    ? 'common.delete.fail.fkConstraint'
                    : 'station.delete.fail.internal',
                type: AlertMessageEnum.ERROR,
              })
            )
          }
        }
        break
      default:
        break
    }
    dispatch(setActionProcessing(false))
  }

  const body = (
    <Form onFinish={onFinish} form={form}>
      {(!management.alertMessage ||
        management.alertMessage.type !== AlertMessageEnum.SUCCESS) && (
        <>
          {(readOnlyMode || management.action === ActionEnum.UPDATE) && (
            <StyledFormItem
              label={
                <StyledLabel>{translate('station.id', language)}</StyledLabel>
              }
            >
              {selectedStation?.id}
            </StyledFormItem>
          )}
          {readOnlyMode && (
            <>
              <StyledFormItem
                label={
                  <StyledLabel>
                    {translate('station.name', language)}
                  </StyledLabel>
                }
              >
                {selectedStation?.name}
              </StyledFormItem>
              <StyledFormItem
                label={
                  <StyledLabel>
                    {translate('station.address', language)}
                  </StyledLabel>
                }
              >
                {selectedStation?.address}
              </StyledFormItem>
            </>
          )}
          {!readOnlyMode && (
            <>
              <StyledFormItem
                name="name"
                label={
                  <StyledLabel>
                    {translate('station.name', language)}
                  </StyledLabel>
                }
                initialValue={selectedStation?.name}
                rules={[
                  {
                    required: true,
                    message: '',
                  },
                  () => ({
                    validator(_, value) {
                      if (!value || !value.trim().length) {
                        return Promise.reject(
                          new Error(translate('station.invalid.name', language))
                        )
                      }
                      return Promise.resolve()
                    },
                  }),
                ]}
              >
                <Input
                  placeholder={translate('station.name', language)}
                  allowClear
                />
              </StyledFormItem>
              <StyledFormItem
                name="address"
                label={
                  <StyledLabel>
                    {translate('station.address', language)}
                  </StyledLabel>
                }
                initialValue={selectedStation?.address}
                rules={[
                  {
                    required: true,
                    message: '',
                  },
                  () => ({
                    validator(_, value) {
                      if (!value || !value.trim().length) {
                        return Promise.reject(
                          new Error(
                            translate('station.invalid.address', language)
                          )
                        )
                      }
                      return Promise.resolve()
                    },
                  }),
                ]}
              >
                <Input
                  placeholder={translate('station.address', language)}
                  allowClear
                />
              </StyledFormItem>
            </>
          )}
          {(readOnlyMode || management.action === ActionEnum.UPDATE) && (
            <StyledFormItem
              label={
                <StyledLabel>
                  {translate('station.owner', language)}
                </StyledLabel>
              }
            >
              {selectedStation?.owner.name}
            </StyledFormItem>
          )}
          {management.action === ActionEnum.CREATE && (
            <StyledFormItem
              name="ownerId"
              label={
                <StyledLabel>
                  {translate('station.owner', language)}
                </StyledLabel>
              }
              initialValue={selectedStation?.owner.id}
              rules={[
                {
                  required: true,
                  message: translate('station.required.owner', language),
                },
              ]}
            >
              <Select
                placeholder={translate('station.owner', language)}
                style={{ width: '100%' }}
                loading={!isUsersLoaded}
                disabled={!isUsersLoaded}
              >
                {users
                  .filter((item) => item.userType.type === UserTypeEnum.OWNER)
                  .map((item) => (
                    <Select.Option key={item.id} value={item.id}>
                      {`${item.name} ( ${item.address} )`}
                    </Select.Option>
                  ))}
              </Select>
            </StyledFormItem>
          )}
        </>
      )}
      <ManageModalFooterComponent
        hasDefaultButtons
        onDeleteConfirm={() => form.submit()}
      />
    </Form>
  )

  return <ManageModalComponent body={body} />
}
